import React from 'react';
import styled from 'styled-components';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Cell,
  ReferenceLine
} from 'recharts';

function SentimentByUserChart({ sentimentData }) {
  if (!sentimentData || !sentimentData.userSentiment) {
    return null;
  }

  // Filter out users with no data
  const chartData = sentimentData.userSentiment.filter(user => user.averageScore !== null);

  if (chartData.length === 0) {
    return (
      <Container>
        <Title>Sentiment by User</Title>
        <EmptyMessage>No user sentiment data available</EmptyMessage>
      </Container>
    );
  }

  const getBarColor = (score) => {
    if (score > 0.3) return '#10b981'; // Green - positive
    if (score < -0.3) return '#ef4444'; // Red - negative
    return '#f59e0b'; // Orange - neutral
  };

  return (
    <Container>
      <Title>Sentiment by User</Title>
      <Description>
        Average sentiment score for each user (-1 = very negative, +1 = very positive)
      </Description>

      <ResponsiveContainer width="100%" height={400}>
        <BarChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 80
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="userName"
            tick={{ fontSize: 12, fill: '#666', dy: 5 }}
            angle={-45}
            textAnchor="end"
            height={100}
          />
          <YAxis
            domain={[-1, 1]}
            ticks={[-1, -0.5, 0, 0.5, 1]}
            tick={{ fontSize: 12, fill: '#666' }}
            label={{
              value: 'Sentiment Score',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fill: '#666', fontSize: '12px' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine y={0} stroke="#9ca3af" strokeDasharray="3 3" />
          <Bar
            dataKey="averageScore"
            radius={[4, 4, 0, 0]}
            maxBarSize={60}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry.averageScore)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      <Legend>
        <LegendItem>
          <LegendDot $color="#10b981" />
          <span>Positive (&gt; 0.3)</span>
        </LegendItem>
        <LegendItem>
          <LegendDot $color="#f59e0b" />
          <span>Neutral (-0.3 to 0.3)</span>
        </LegendItem>
        <LegendItem>
          <LegendDot $color="#ef4444" />
          <span>Negative (&lt; -0.3)</span>
        </LegendItem>
      </Legend>
    </Container>
  );
}

// Custom tooltip
function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const score = data.averageScore;

    let sentiment = 'Neutral';
    if (score > 0.3) sentiment = 'Positive';
    else if (score < -0.3) sentiment = 'Negative';

    return (
      <TooltipContainer>
        <TooltipLabel>{data.userName}</TooltipLabel>
        <TooltipValue>
          <strong>Score:</strong> {score > 0 ? '+' : ''}{score}
        </TooltipValue>
        <TooltipValue>
          <strong>Sentiment:</strong> {sentiment}
        </TooltipValue>
        <TooltipValue>
          <strong>Comments:</strong> {data.commentCount}
        </TooltipValue>
      </TooltipContainer>
    );
  }
  return null;
}

const Container = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 2rem;
  padding: 1.5rem;
`;

const Title = styled.h3`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
`;

const Description = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  line-height: 1.5;
  margin: 0 0 1.5rem 0;
`;

const EmptyMessage = styled.p`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  padding: 2rem;
  text-align: center;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1rem;
`;

const LegendItem = styled.div`
  align-items: center;
  color: var(--light-brown);
  display: flex;
  font-family: 'Inter', sans-serif;
  font-size: 0.875rem;
  gap: 0.5rem;
`;

const LegendDot = styled.div`
  background: ${props => props.$color};
  border-radius: 50%;
  flex-shrink: 0;
  height: 12px;
  width: 12px;
`;

const TooltipContainer = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 0.75rem;
`;

const TooltipLabel = styled.div`
  color: var(--brown);
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const TooltipValue = styled.div`
  color: var(--light-brown);
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  margin: 0.25rem 0;
`;

export default SentimentByUserChart;
